// SEO head helpers — this SPA ships one static index.html, so per-route
// canonical links and JSON-LD blocks are managed imperatively via hooks.
// All absolute URLs use the production origin so markup is correct after
// the DNS cutover (never the preview hostname).
import { useEffect } from "react";

export const SITE_ORIGIN = "https://loadout.co.in";

const DEFAULT_TITLE = "Loadout — Gaming Peripherals, Handpicked for India";
const DEFAULT_DESCRIPTION =
  "Operator-grade gaming keyboards, mice, and surfaces — handpicked and built for the Indian esports operator.";

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!value) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function applyMeta({ title, description, image, path, type, noindex }) {
  const t = title || DEFAULT_TITLE;
  const d = description || DEFAULT_DESCRIPTION;
  document.title = t;
  setMeta("name", "description", d);
  setMeta("property", "og:title", t);
  setMeta("property", "og:description", d);
  setMeta("property", "og:type", type || "website");
  setMeta("property", "og:url", path != null ? `${SITE_ORIGIN}${path}` : null);
  setMeta("property", "og:image", image || null);
  setMeta("name", "twitter:title", t);
  setMeta("name", "twitter:description", d);
  setMeta("name", "twitter:image", image || null);
  setMeta("name", "robots", noindex ? "noindex,follow" : "index,follow");
}

// Per-route <title>, description, Open Graph/Twitter and robots tags.
// Pass null to leave the head untouched (e.g. while data loads).
export function useSeoMeta(meta) {
  const key = meta ? JSON.stringify(meta) : null;
  useEffect(() => {
    if (!key) return undefined;
    applyMeta(JSON.parse(key));
    return () => applyMeta({});
  }, [key]);
}

export function clipText(s, max = 158) {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 80 ? cut.lastIndexOf(" ") : cut.length)}…`;
}

export function useCanonical(path) {
  useEffect(() => {
    if (!path) return undefined;
    // Guarantee exactly ONE canonical per page.
    document.head
      .querySelectorAll('link[rel="canonical"]')
      .forEach((el) => el.remove());
    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = `${SITE_ORIGIN}${path}`;
    link.setAttribute("data-seo", "canonical");
    document.head.appendChild(link);
    return () => link.remove();
  }, [path]);
}

export function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!json) return undefined;
    const prev = document.head.querySelector(
      `script[type="application/ld+json"][data-seo-id="${id}"]`,
    );
    if (prev) prev.remove();
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    el.textContent = json;
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, json]);
}
